import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { CheckCircle, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Authenticated password-rotation dialog. Used by Dr. Agyekum (and any user)
 * to swap the dev seed password for a production-strength secret without
 * needing to go through the email-reset flow.
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setError(null); setSuccess(false); setSaving(false);
  };

  const submit = async () => {
    setError(null); setSuccess(false);
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true },
      );
      setSuccess(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Password change failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Rotate your password to a production-strength secret. Minimum 8 characters.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
              Current password
            </label>
            <Input
              type="password" value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
              New password
            </label>
            <Input
              type="password" value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
              Confirm new password
            </label>
            <Input
              type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="confirm-password-input"
            />
          </div>

          {error && (
            <p className="text-[12px] text-[#9B1C1C] flex items-center gap-1.5" data-testid="change-password-error">
              <Warning size={13} /> {error}
            </p>
          )}
          {success && (
            <p className="text-[12px] text-[#03543F] flex items-center gap-1.5" data-testid="change-password-success">
              <CheckCircle size={13} /> Password changed successfully.
            </p>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button
            variant="outline" onClick={() => { reset(); onOpenChange(false); }}
            data-testid="change-password-cancel"
          >
            Close
          </Button>
          <Button
            onClick={submit} disabled={saving}
            className="bg-[#0B1426] hover:bg-[#1B2538] text-white"
            data-testid="change-password-submit"
          >
            {saving ? 'Saving…' : 'Change password'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
